import { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../store/store';
import {
  toggleTheme,
  setThemePreference,
  systemThemeChanged,
} from '../store/slices/themeSlice';
import { watchSystemTheme } from '../utils/theme';
import { ThemePreference } from '../types/user';

/**
 * The rendered theme and the user's choice behind it (#378).
 *
 * `isDarkMode` is what components paint with; `preference` is what settings
 * shows as selected. They differ whenever the user chose `system`.
 */
export const useTheme = () => {
  const dispatch = useDispatch();
  const mode = useSelector((state: RootState) => state.theme.mode);
  const preference = useSelector((state: RootState) => state.theme.preference);

  // Follow the OS only while the user asked to. Someone who picked light or
  // dark explicitly should not have the app change under them when their
  // machine switches at sunset.
  useEffect(() => {
    if (preference !== 'system') {
      return;
    }
    return watchSystemTheme((next) => dispatch(systemThemeChanged(next)));
  }, [preference, dispatch]);

  const toggle = () => {
    dispatch(toggleTheme());
  };

  const setPreference = (next: ThemePreference) => {
    dispatch(setThemePreference(next));
  };

  return {
    mode,
    preference,
    toggle,
    setPreference,
    isDarkMode: mode === 'dark',
  };
};
